import { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import type { OpdrachtRij, OpdrachtgeverRij } from '@vip/shared';

import { AdministratieWaarschuwing } from '@/components/AdministratieWaarschuwing';
import { OpdrachtenKiezer } from '@/components/OpdrachtenKiezer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../lib/apiClient';

/**
 * Op welke opdrachten iemand zit: aanvinken wat er is, of er een aanmaken.
 *
 * Eén venster voor de Admin op de personenpagina en voor de buddy in zijn
 * roster. Twee dingen verschillen en staan daarom als prop:
 *
 * - **Waar een nieuwe opdracht heen gaat.** De buddy mag er een laten ontstaan
 *   via zijn eigen route, de Admin via de zijne. Het venster kent die routes
 *   niet en krijgt de aanroep mee.
 * - **De waarschuwing.** Bij de buddy is dit een ingreep in de administratie
 *   buiten zijn gesprek om en hoort er een bevestiging tussen. Bij de Admin
 *   is het gewoon zijn werk.
 *
 * Opslaan is een aparte druk op de knop en gebeurt niet al bij het aanvinken:
 * dit verandert wat er elders in de app over deze persoon staat.
 */
export function OpdrachtenDialog({
  naam,
  opdrachten,
  opdrachtgevers,
  open,
  bezig,
  waarschuwing,
  onMaakOpdracht,
  onBewaar,
  onSluit,
  onFout,
}: {
  naam: string;
  opdrachten: readonly OpdrachtRij[];
  opdrachtgevers: readonly OpdrachtgeverRij[];
  open: boolean;
  bezig?: boolean;
  /** Tekst voor de bevestiging; zonder deze prop wordt er meteen opgeslagen. */
  waarschuwing?: string;
  onMaakOpdracht: (invoer: {
    opdrachtgever: string;
    opdracht: string;
  }) => Promise<{ opdrachtId: string }>;
  onBewaar: (opdrachtIds: string[]) => void;
  onSluit: () => void;
  onFout: (melding: string) => void;
}) {
  const [keuze, setKeuze] = useState<string[]>(opdrachten.map((o) => o.id));
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [gever, setGever] = useState('');
  const [opdrachtNaam, setOpdrachtNaam] = useState('');
  const [maakt, setMaakt] = useState(false);
  const [bevestigen, setBevestigen] = useState(false);

  const huidig = opdrachten.map((o) => o.id);
  const gewijzigd = keuze.length !== huidig.length || keuze.some((id) => !huidig.includes(id));

  function herstel() {
    setKeuze(opdrachten.map((o) => o.id));
    setNieuwOpen(false);
    setGever('');
    setOpdrachtNaam('');
    setBevestigen(false);
  }

  async function maakAan() {
    if (!gever.trim() || !opdrachtNaam.trim()) return;
    onFout('');
    setMaakt(true);
    try {
      const { opdrachtId } = await onMaakOpdracht({
        opdrachtgever: gever,
        opdracht: opdrachtNaam,
      });
      // Meteen aangevinkt, maar nog niet opgeslagen op de persoon: de knop
      // hieronder blijft de enige plek waar dat gebeurt.
      setKeuze((was) => [...was, opdrachtId]);
      setNieuwOpen(false);
      setGever('');
      setOpdrachtNaam('');
    } catch (err) {
      onFout(err instanceof ApiError ? err.message : String(err));
    } finally {
      setMaakt(false);
    }
  }

  return (
    <Dialog onOpenChange={(o) => !o && onSluit()} open={open}>
      <DialogContent className="max-h-[75vh] overflow-y-auto sm:max-w-md" onOpenAutoFocus={herstel}>
        <DialogHeader>
          <DialogTitle>Opdrachten van {naam}</DialogTitle>
        </DialogHeader>

        <OpdrachtenKiezer
          gekozen={keuze}
          idPrefix="opdrachten-dialog"
          onWissel={(id, aan) =>
            setKeuze((was) => (aan ? [...was, id] : was.filter((x) => x !== id)))
          }
          opdrachtgevers={opdrachtgevers}
        />

        {nieuwOpen ? (
          <Field>
            <FieldLabel htmlFor="nieuwe-opdrachtgever">Nieuwe opdracht</FieldLabel>
            <div className="flex flex-wrap gap-2">
              <Input
                autoFocus
                className="w-full sm:w-40"
                id="nieuwe-opdrachtgever"
                onChange={(e) => setGever(e.target.value)}
                placeholder="Opdrachtgever"
                value={gever}
              />
              <Input
                aria-label="Naam van de opdracht"
                className="w-full sm:w-40"
                onChange={(e) => setOpdrachtNaam(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void maakAan()}
                placeholder="Opdracht"
                value={opdrachtNaam}
              />
              <Button
                disabled={!gever.trim() || !opdrachtNaam.trim() || maakt}
                onClick={() => void maakAan()}
                size="sm"
              >
                {maakt && <Spinner data-icon="inline-start" />}
                Aanmaken
              </Button>
              <Button onClick={() => setNieuwOpen(false)} size="sm" variant="ghost">
                Annuleren
              </Button>
            </div>
          </Field>
        ) : (
          <Button
            className="self-start"
            onClick={() => setNieuwOpen(true)}
            size="sm"
            variant="outline"
          >
            <PlusIcon data-icon="inline-start" />
            Nieuwe opdracht
          </Button>
        )}

        <DialogFooter>
          <Button
            disabled={!gewijzigd || bezig}
            onClick={() => (waarschuwing ? setBevestigen(true) : onBewaar(keuze))}
          >
            {bezig && <Spinner data-icon="inline-start" />}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>

      {waarschuwing && (
        <AdministratieWaarschuwing
          actie={waarschuwing}
          onAnnuleer={() => setBevestigen(false)}
          onBevestig={() => {
            setBevestigen(false);
            onBewaar(keuze);
          }}
          open={bevestigen}
        />
      )}
    </Dialog>
  );
}
